import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

export interface ModeratorFormValue {
  mode?: 'existing' | 'new';
  existing_moderator_id?: number;
  name: string;
  company?: string;
  job_title?: string;
  email?: string;
  phone_number?: string;
  linkedin_url?: string;
}

@Component({
  selector: 'app-moderator-modal',
  standalone: false,
  templateUrl: './moderator-modal.component.html',
})
export class ModeratorModalComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() moderator: any = null;  // null = add mode, object = edit mode
  @Input() allModerators: any[] = [];
  @Input() linkedModeratorIds: number[] = [];
  @Input() allowExistingSelect = true;
  @Input() saving = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<ModeratorFormValue>();
  @Output() cancel = new EventEmitter<void>();

  form: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      mode: ['new'],
      existing_moderator_id: [null],
      name: [null, [Validators.required]],
      company: [null],
      job_title: [null],
      email: [null, [Validators.email]],
      phone_number: [null],
      linkedin_url: [null]
    });
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.form.reset();
      if (this.moderator) {
        this.form.patchValue({
          mode: 'new',
          existing_moderator_id: null,
          name: this.moderator.name,
          company: this.moderator.company,
          job_title: this.moderator.job_title,
          email: this.moderator.email,
          phone_number: this.moderator.phone_number,
          linkedin_url: this.moderator.linkedin_url,
        });
      } else {
        this.form.patchValue({ mode: this.allowExistingSelect ? 'existing' : 'new' });
      }
    }
  }

  get isEditMode(): boolean {
    return !!this.moderator;
  }

  get isExistingMode(): boolean {
    return !this.isEditMode && this.allowExistingSelect && this.form.get('mode')?.value === 'existing';
  }

  isAlreadyLinked(id: number): boolean {
    return this.linkedModeratorIds.includes(id);
  }

  onOk(): void {
    if (this.isExistingMode) {
      const existingId = this.form.get('existing_moderator_id')?.value;
      if (!existingId) {
        this.form.get('existing_moderator_id')?.markAsDirty();
        this.form.get('existing_moderator_id')?.updateValueAndValidity();
        return;
      }
      this.save.emit({
        mode: 'existing',
        existing_moderator_id: Number(existingId)
      } as ModeratorFormValue);
      return;
    }

    if (this.form.invalid) {
      Object.values(this.form.controls).forEach(c => {
        c.markAsDirty();
        c.updateValueAndValidity();
      });
      return;
    }
    const { mode, existing_moderator_id, ...payload } = this.form.value;
    this.save.emit({ mode: 'new', ...payload });
  }

  onCancel(): void {
    this.form.reset();
    this.visibleChange.emit(false);
    this.cancel.emit();
  }
}
